import Vue from 'vue';
import Router from 'vue-router';

import store from "./store";
import localStorageService from "./services/localStorage";
import api from "./api";
import {initPusher} from './services/pusher';
import Echo from 'laravel-echo';

window.Pusher = require('pusher-js');

import LoginPage from "../client/modules/auth/LoginPage";
import RegisterPage from "../client/modules/auth/RegisterPage";
import PasswordRequest from "../client/modules/auth/PasswordRequest";
import PasswordReset from "../client/modules/auth/PasswordReset";
import AcceptInvitePage from "../client/modules/account/AcceptInvitePage";
import SetupNewAccountPage from "../client/modules/account/SetupNewAccountPage";
import ProvisioningNoticePage from "./modules/account/ProvisioningNoticePage";
import CallScriptPublicPage from "../client/modules/settings/CallScriptPublicPage";

import InboxPage from "../client/modules/inbox/InboxPage";
import PipelinePage from "../client/modules/pipeline/PipelinePage";
import StatsPage from "../client/modules/stats/StatsPage";
import CampaignsPage from "../client/modules/campaigns/CampaignsPage";
import CommsPage from "../client/modules/comms/CommsPage";
import UpNextPage from "../client/modules/up-next/UpNextPage";

import ContactsPage from "../client/modules/contacts/ContactsPage";
import SettingsPage from "../client/modules/settings/SettingsPage";
import StorePage from "../client/modules/store/StorePage";
import {BillingSpecials} from "./services/billingSpecials";
import TestErrorPage from "./modules/auth/TestErrorPage";
import UserDashboardPage from "./modules/user-dashboard/UserDashboardPage";
import PropertyPrintPage from "./modules/print/PropertyPrintPage.vue";
import OnboardingWelcomePage from "./modules/onboarding/OnboardingWelcomePage.vue";
import OnboardingWizard1Page from "./modules/onboarding/OnboardingWizard1Page.vue";
import OnboardingWizard2Page from "./modules/onboarding/OnboardingWizard2Page.vue";
import OnboardingWizard3Page from "./modules/onboarding/OnboardingWizard3Page.vue";
import OnboardingWhiteGlovePage from "./modules/onboarding/OnboardingWhiteGlovePage.vue";
import OnboardingProvisioningPage from "./modules/onboarding/OnboardingProvisioningPage.vue";
import LeadsPage from "./modules/leads/LeadsPage.vue";
import ThirdPartyLogin from "./modules/auth/ThirdPartyLogin.vue";
import LeadPage from "./modules/lead/LeadPage.vue";

Vue.use(Router);


/**
 * This is here because Vue Router is throwing an error whenever we go
 * close a modal we use $router.push function to go from /pipeline?lead=123 to /pipeline
 */
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(error => {
    if (error.name !== 'NavigationDuplicated' || error.name !== 'NavigationRedirected') throw error;
  });
};

/**
 * Since we use replace to just change the query or params and we're still getting errors
 * I'm going remove all errors for replace
 */
const originalReplace = Router.prototype.replace;
Router.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(() => {});
};

let router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  linkActiveClass: "parent",
  linkExactActiveClass: "is-active",
  routes: [
    {path: '/', redirect: {name: 'user-dashboard'}},
    {
      path: '/lead',
      name: 'lead',
      component: LeadPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/third-party-login',
      name: 'third-party-login',
      component: ThirdPartyLogin,
      meta: {
        logoutFirst: true,
        requiresAuth: false,
      }
    },
    {
      path: '/login',
      name: 'login',
      component: LoginPage,
      meta: {
        requiresAuth: false,
      }
    },
    {
      path: '/password-request',
      name: 'password-request',
      component: PasswordRequest,
      meta: {
        requiresAuth: false,
      }
    },
    {
      path: '/password-reset/:resetToken',
      name: 'password-reset',
      component: PasswordReset,
      meta: {
        requiresAuth: false,
      }
    },
    {
      path: '/accept-invite/:inviteToken',
      name: 'accept-invite',
      component: AcceptInvitePage,
      meta: {
        requiresAuth: false,
      }
    },
    {
      path: '/account-setup',
      name: 'account-setup',
      component: SetupNewAccountPage,
      meta: {
        requiresAuth: false,
      }
    },
    {
      path: '/onboarding',
      name: 'onboarding-welcome',
      component: OnboardingWelcomePage,
      meta: {
        requiresAuth: true,
      }
    },
    {
      path: '/onboarding/wizard/1',
      name: 'onboarding-wizard-1',
      component: OnboardingWizard1Page,
      meta: {
        requiresAuth: true,
      }
    },
    {
      path: '/onboarding/wizard/2',
      name: 'onboarding-wizard-2',
      component: OnboardingWizard2Page,
      meta: {
        requiresAuth: true,
      }
    },
    {
      path: '/onboarding/wizard/3',
      name: 'onboarding-wizard-3',
      component: OnboardingWizard3Page,
      meta: {
        requiresAuth: true,
      }
    },
    {
      path: '/onboarding/white-glove',
      name: 'onboarding-white-glove',
      component: OnboardingWhiteGlovePage,
      meta: {
        requiresAuth: true,
      }
    },
    {
      path: '/onboarding/provisioning',
      name: 'onboarding-provisioning',
      component: OnboardingProvisioningPage,
      meta: {
        requiresAuth: true,
      }
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterPage,
      meta: {
        requiresAuth: false,
      }
    },
    ...BillingSpecials.routes(),
    {
      path: '/account-provisioning',
      name: 'account-provisioning',
      component: ProvisioningNoticePage,
      meta: {
        requiresAuth: true,
      }
    },
    {
      path: '/user-dashboard',
      name: 'user-dashboard',
      component: UserDashboardPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/inbox/:tab?',
      name: 'inbox',
      component: InboxPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/pipeline/:tab?',
      name: 'pipeline',
      component: PipelinePage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/leads',
      name: 'leads',
      component: LeadsPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/print/property',
      name: 'print-property',
      component: PropertyPrintPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/contacts',
      name: 'contacts',
      component: ContactsPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/stats/:tab?',
      name: 'stats',
      component: StatsPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/campaigns',
      name: 'campaigns',
      component: CampaignsPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/comms',
      name: 'comms',
      component: CommsPage,
      meta: {
        requiresAuth: true,
        requiresPermission: 'can_access_comm_log_page'
      }
    },
    {
      path: '/up-next/:tab?',
      name: 'up-next',
      component: UpNextPage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/store/:tab?',
      name: 'store',
      component: StorePage,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/settings/:tab?/:page?',
      name: 'settings',
      component: SettingsPage,
      meta: {
        requiresAuth: true,
        requiresPermission: 'can_access_settings'
      }
    },
    {
      path: '/script/:identifier',
      name: 'call-script-public',
      component: CallScriptPublicPage,
      meta: {
        exposed: true,
      }
    },
    /**
     * This page allows you to send a test error to Sentry
     *
     * Keep it commented out unless you are using it
     */
    // {
    //   path: '/test-error',
    //   component: TestErrorPage,
    //   meta: {
    //     requiresAuth: true,
    //   }
    // },
    {path: '*', redirect: '/'}
  ]
});

// Add navigation guard to handle lead query parameter
router.beforeResolve((to, from, next) => {
  console.log('Navigation Debug:', {
    fromRoute: from.name,
    toRoute: to.path,
    toQuery: to.query,
    historyLength: window.history.length,
    historyState: window.history.state,
    performance: window.performance.getEntriesByType('navigation')[0]?.type
  });

  // The history length is typically 2 on initial page load (1 for initial + 1 for current)
  const isInitialNavigation = !from.name && window.history.length <= 2;

  console.log('Is Initial Navigation:', isInitialNavigation);

  if (isInitialNavigation &&
      to.query.lead &&
      to.path !== '/lead' &&
      !to.matched.some(record => record.meta.requiresAuth === false)) {

    console.log('Redirecting to lead page');

    // Replace the current history entry instead of adding a new one
    window.history.replaceState({}, '', `/lead?lead=${to.query.lead}`);

    next({
      path: '/lead',
      query: { lead: to.query.lead },
      replace: true
    });
    return;
  }
  next();
});

// Redirect users based on route meta and if authenticated
router.beforeEach((to, from, next) => {
  if (localStorageService.getAuthUser() == null) {
    store.commit('setIsLoggedIn', false);
  } else {
    store.commit('setIsLoggedIn', true);
  }

  // If route is exposed to anyone route
  if (to.matched.some(route => route.meta.exposed === true)) {
    next();
  }

  if (to.matched.some(route => route.meta.logoutFirst === true)) {
    console.log('logout first')
    store.commit('setIsLoggedIn', false);
    next();

    api.auth.logout();
  }

  // If Unauthorized route
  if (to.matched.some(route => route.meta.requiresAuth === false)) {
    // If really unauthenticated
    if (!store.state.isLoggedIn) {
        const initData = api.clientStore.initData().then(response => {
          store.commit('setInitData', response.data);
        });

        Promise.all([
          initData,
        ]).then(() => {
          next();
        });
    }
    // If actually authenticated
    else {
      next('/');
    }
  }

  // // If route requires permission
  let authUser = localStorageService.getAuthUser();
  if (to.matched.some(route => route.meta.requiresPermission)) {
    let permission = to.meta.requiresPermission;
    if (!authUser.is_owner && authUser[permission] === false) {
      next('/');
    }
  }

  // If route requires authentication
  if (to.matched.some(route => route.meta.requiresAuth === true)) {
    // Check if not authenticated
    if (!store.state.isLoggedIn) {
      next({
        path: '/login',
        params: {nextUrl: to.fullPath}
      });
    }

    // Continue if authenticated
    else {
      if (!store.state.appIsInitiated) {
        let isOnboarded = true;

        const initData = api.clientStore.initData().then(response => {
          store.commit('setInitData', response.data);
        });

        const initEnums = api.clientStore.initEnums().then(response => {
          const enums = {};

          for (let property in response.data) {
            enums[property] = response.data[property];
          }

          store.commit('setEnums', enums);
        });

        const initAccount = api.clientStore.initAccountStepOne(localStorageService.getAuthUser().account_id).then(response => {
          store.commit('setAccount',{
            ...response.data,
            fusTemplates: [],
            callScripts: [],
            tableConfigurations: [],
            pipeline_counts: []
          });
          store.commit('accounts/updateFeatures', response.data.account);

          // If there is an onboarding check to is_complete if not default to true;
          isOnboarded = response.data.account.onboarding ? response.data.account.onboarding.is_complete : true;

          const activeUser = store.getters.users.find(u => u.id === localStorageService.getAuthUser().id);
          store.commit('setActiveUser', activeUser);

          api.clientStore.initAccountStepTwo(localStorageService.getAuthUser().account_id).then(response => {
            store.commit('setAccount', {
              ...store.state.account,
              ...response.data
            });
          });
        });

        const initMarket = api.clientStore.initMarketStepOne(localStorageService.getAuthUser().account_id).then(response => {
          store.commit('setMarket', {
            ...response.data,
            inboxLeadsItemLite: [],
            inboxInboundCommsItemLite: [],
            inboxInboundCommsItemLiteComms: [],
            incompleteTasks: [],
            pendingAppointments: []
          });


          // Dont hold up app render for this subset of data
          api.clientStore.initMarketStepTwo(localStorageService.getAuthUser().account_id).then(response => {
            store.commit('setMarket', {
              ...store.state.market,
              ...response.data
            });
          });

          api.clientStore.initMarketStepThree(localStorageService.getAuthUser().account_id).then(response => {
            store.commit('setMarket', {
              ...store.state.market,
              ...response.data
            });
          });
        });

        const initUser = api.clientStore.initUser(localStorageService.getAuthUser().id).then(response => {
          store.commit('setUser', response.data);
        });

        const initUserAppFilter = api.users.getFilterData(localStorageService.getAuthUser().id).then(response => {
          store.commit('setUserAppFilters', response.data.data.filters);
        })

        Promise.all([
          initData,
          initAccount,
          initEnums,
          initMarket,
          initUser,
          initUserAppFilter
        ]).then(() => {
          initPusher();

          // Echo setup
          window.Echo = new Echo({
            broadcaster: 'pusher',
            key: process.env.MIX_PUSHER_APP_KEY,
            cluster: process.env.MIX_PUSHER_APP_CLUSTER,
            forceTLS: true,
            auth: {
              headers: {
                Authorization: 'Bearer ' + localStorageService.getApiToken()
              },
            },
          });

          store.commit('setAppIsInitiated', true);

          if(isOnboarded) {
            next();
          } else {
            next('/onboarding')
          }
        });

      } else {
        next();
      }
    }
  }
});

export default router;

